import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

/**
 * GroupedPortalNav - Condenses many flat tabs into 3 top-level groups with sub-item chips.
 * Pure navigation wrapper: does not touch any underlying tab content/state logic.
 */
export function GroupedPortalNav({ groups, activeTab, onTabChange, onExternalNav, className }) {
  const findGroupForTab = (tabId) => groups.find(g => g.subItems.some(s => s.id === tabId)) || groups[0];
  const [activeGroupId, setActiveGroupId] = useState(findGroupForTab(activeTab).id);

  useEffect(() => {
    const g = findGroupForTab(activeTab);
    if (g && g.id !== activeGroupId) setActiveGroupId(g.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const activeGroup = groups.find(g => g.id === activeGroupId) || groups[0];

  const handleSubItemClick = (item) => {
    if (item.path) { onExternalNav(item.path); return; }
    onTabChange(item.id);
  };

  return (
    <div className={cn('mb-3 sm:mb-4', className)} data-testid="grouped-portal-nav">
      {/* Primary 3-tab group row */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl overflow-x-auto" data-testid="nav-primary-groups">
        {groups.map(g => {
          const Icon = g.icon;
          const isActive = activeGroupId === g.id;
          return (
            <button
              key={g.id}
              onClick={() => {
                setActiveGroupId(g.id);
                const current = g.subItems.find(s => s.id === activeTab);
                if (!current) handleSubItemClick(g.subItems[0]);
              }}
              data-testid={`nav-group-${g.id}`}
              className={cn(
                'flex-1 shrink-0 min-w-[92px] sm:min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-4 py-2 sm:py-2.5 rounded-lg text-[10px] sm:text-sm font-semibold transition-all whitespace-nowrap',
                isActive ? 'bg-white text-[#2BB673] shadow-sm' : 'text-[#4A4D52] hover:text-[#1A1D1F] hover:bg-white/60'
              )}
            >
              {Icon && <Icon className={cn('w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0', isActive ? 'text-[#2BB673]' : 'text-slate-400')} />}
              <span className="truncate">{g.label}</span>
            </button>
          );
        })}
      </div>

      {/* Secondary sub-item chip row for active group */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 pt-2 -mx-1 px-1" data-testid={`nav-subitems-${activeGroupId}`}>
        {activeGroup.subItems.map(item => {
          const Icon = item.icon;
          const isActive = !item.path && activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleSubItemClick(item)}
              data-testid={`nav-subitem-${item.id}`}
              className={cn(
                'flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap transition-colors shrink-0',
                isActive ? 'bg-[#2BB673] text-white' : 'bg-slate-100 text-[#4A4D52] hover:bg-slate-200'
              )}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
